import React from 'react';
import { MODE_LABEL, type SearchView } from '../hooks/useSearchViews';

/* The "View:" picker shared by the Custom Search and Custom Filter modals.
 *
 * Two labelled groups in one control:
 *   - "System Views"  the manager-published, org-wide layouts (is_view=1)
 *   - "My Views"      the caller's OWN layouts (is_view=0, "templates" in code)
 *
 * TERMINOLOGY: the UI calls an is_view=1 row a "System View" and an is_view=0 row
 * a "View". The code, the DB column and the endpoints keep the original names, so
 * read every "template" identifier here as the user-facing "View".
 *
 * `optgroup` rather than a hand-rolled separator: it gives the divider and the
 * label natively in every browser, and — unlike a disabled `<option>` used as a
 * heading — it cannot be selected or keyboard-landed on. Both groups carry a
 * label so a bare list of names is never ambiguous about which kind it is.
 *
 * "My Views" lists only the caller's own. A colleague's SHARED layout is
 * deliberately absent (useSearchViews filters on is_mine); it stays reachable from
 * Load View, where it is credited to them. Here it would read as a System View.
 *
 * Renders nothing when there is nothing to offer — no System Views and none of
 * the caller's own — so neither modal grows a control whose only entry is
 * "Default".
 *
 * NOT V1's "View" picker, which is `searchdisplay` (defs.pl ~3580), a fixed
 * ACL-gated list of display GRAINS. Same word, different feature. */

interface Props {
  /** Manager-published, org-wide layouts — shown under "System Views". */
  views: SearchView[];
  /** The caller's own layouts — shown under "My Views". */
  myViews?: SearchView[];
  /** Selected row id, or '' for the user's own current layout. */
  value: string;
  onChange: (viewId: string) => void;
  className?: string;
}

const SearchViewSelect: React.FC<Props> = ({
  views,
  myViews = [],
  value,
  onChange,
  className,
}) => {
  if (views.length === 0 && myViews.length === 0) return null;

  // Both kinds are rows in one table, so a single numeric id identifies either
  // and `?view=<id>` needs no kind marker.
  const option = (v: SearchView) => (
    <option key={v.id} value={String(v.id)}>
      {v.name} ({MODE_LABEL[v.mode]})
    </option>
  );

  return (
    <label className="flex items-center gap-1.5 text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300">
      View:
      <select
        aria-label="View"
        className={
          className ??
          'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] font-normal bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {/* '' is "leave my own layout alone" — the results grid then loads the
            user's own saved settings exactly as it does today. */}
        <option value="">Default</option>
        {views.length > 0 && <optgroup label="System Views">{views.map(option)}</optgroup>}
        {myViews.length > 0 && <optgroup label="My Views">{myViews.map(option)}</optgroup>}
      </select>
    </label>
  );
};

export default SearchViewSelect;
